import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Plus, PencilSimple, Copy, Archive, Eye, X, Tag, TrendUp, Users } from "@phosphor-icons/react";

const D = (c) => (c || 0) / 100;
const C = (d) => Math.round((parseFloat(d) || 0) * 100);
const CYCLE_LABEL = { monthly: "Monthly", quarterly: "Quarterly", semiannual: "Semiannual", annual: "Annual" };

const blank = (schema) => ({
  key: "", name: "", tagline: "", badge: "", status: "active", contact_sales: false, currency: "USD",
  pricing: { monthly: 0, quarterly: 0, semiannual: 0, annual: 0 }, setup_fee_cents: 0,
  promo: { enabled: false, price_cents: 0, label: "" }, trial_days: 0,
  availability: { mode: "all", countries: [] },
  features: Object.fromEntries((schema?.feature_keys || []).map(([k]) => [k, false])),
  feature_list: [], limits: Object.fromEntries((schema?.limit_keys || []).map(([k]) => [k, "none"])),
  price_effective: "new_only",
});

export default function AdminAISalesPackages() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [subsFor, setSubsFor] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  const { data: schema } = useQuery({ queryKey: ["ai-pkg-schema"], queryFn: async () => (await api.get("/ai-sales/admin/schema")).data });
  const { data: pkgData } = useQuery({ queryKey: ["ai-pkgs"], queryFn: async () => (await api.get("/ai-sales/admin/packages")).data });
  const { data: addonData } = useQuery({ queryKey: ["ai-addons"], queryFn: async () => (await api.get("/ai-sales/admin/addons")).data });
  const { data: auditData } = useQuery({ queryKey: ["ai-audit"], queryFn: async () => (await api.get("/ai-sales/admin/price-audit")).data, enabled: showAudit });

  const packages = pkgData?.packages || [];
  const totals = pkgData?.totals || {};
  const addons = addonData?.addons || [];

  const save = useMutation({
    mutationFn: async (p) => p._isNew
      ? (await api.post("/ai-sales/admin/packages", p)).data
      : (await api.put(`/ai-sales/admin/packages/${p.key}`, p)).data,
    onSuccess: () => { toast.success("Package saved"); setEditing(null); refresh(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const act = useMutation({
    mutationFn: async ({ key, action, status }) => {
      if (action === "duplicate") return (await api.post(`/ai-sales/admin/packages/${key}/duplicate`)).data;
      return (await api.post(`/ai-sales/admin/packages/${key}/status`, { status })).data;
    },
    onSuccess: () => { toast.success("Done"); refresh(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["ai-pkgs"] }); qc.invalidateQueries({ queryKey: ["ai-plans"] }); };

  const openSubs = async (key) => {
    try { const { data } = await api.get(`/ai-sales/admin/packages/${key}/subscribers`); setSubsFor({ key, rows: data.subscribers }); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-ai-packages">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15 text-primary"><Tag size={24} weight="fill" /></div>
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Packages &amp; Pricing</h1>
            <p className="text-sm text-muted-foreground">Control iYeneh SI Sales commercially — no code, no redeploys.</p>
          </div>
        </div>
        <button data-testid="new-package" onClick={() => setEditing({ ...blank(schema), _isNew: true })}
          className="flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground">
          <Plus size={16} weight="bold" /> New package
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5"><div className="flex items-center gap-2 text-xs font-semibold uppercase text-muted-foreground"><TrendUp size={16} /> MRR</div><div className="mt-2 font-display text-3xl font-semibold">{money(totals.mrr_cents || 0)}</div></div>
        <div className="rounded-2xl border border-border bg-card p-5"><div className="flex items-center gap-2 text-xs font-semibold uppercase text-muted-foreground"><TrendUp size={16} /> ARR</div><div className="mt-2 font-display text-3xl font-semibold">{money(totals.arr_cents || 0)}</div></div>
        <div className="rounded-2xl border border-border bg-card p-5"><div className="flex items-center gap-2 text-xs font-semibold uppercase text-muted-foreground"><Users size={16} /> Active subscribers</div><div className="mt-2 font-display text-3xl font-semibold">{totals.active || 0}</div></div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-accent/50 text-left text-xs uppercase text-muted-foreground">
            <tr><th className="p-3">Package</th><th className="p-3">Price /mo</th><th className="p-3">Subs</th><th className="p-3">Features</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {packages.map((p) => (
              <tr key={p.key} data-testid={`pkg-row-${p.key}`} className="border-t border-border">
                <td className="p-3"><div className="font-semibold">{p.name}</div><div className="text-xs text-muted-foreground">{p.badge || p.key}</div></td>
                <td className="p-3">{p.contact_sales ? "Custom" : money(p.pricing?.monthly || 0)}</td>
                <td className="p-3">{p.analytics?.active || 0}</td>
                <td className="p-3">{Object.values(p.features || {}).filter(Boolean).length} on</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${p.status === "active" ? "bg-emerald-500/15 text-emerald-500" : p.status === "archived" ? "bg-muted text-muted-foreground" : "bg-amber-500/15 text-amber-500"}`}>{p.status}</span></td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    <button title="Edit" data-testid={`edit-${p.key}`} onClick={() => setEditing({ ...p, _isNew: false })} className="rounded-lg p-2 hover:bg-accent"><PencilSimple size={16} /></button>
                    <button title="Duplicate" data-testid={`dup-${p.key}`} onClick={() => act.mutate({ key: p.key, action: "duplicate" })} className="rounded-lg p-2 hover:bg-accent"><Copy size={16} /></button>
                    <button title="Subscribers" data-testid={`subs-${p.key}`} onClick={() => openSubs(p.key)} className="rounded-lg p-2 hover:bg-accent"><Eye size={16} /></button>
                    <button title={p.status === "active" ? "Deactivate" : "Activate"} data-testid={`toggle-${p.key}`} onClick={() => act.mutate({ key: p.key, status: p.status === "active" ? "inactive" : "active" })} className="rounded-lg px-2 py-1 text-xs font-semibold hover:bg-accent">{p.status === "active" ? "Off" : "On"}</button>
                    <button title="Archive" data-testid={`archive-${p.key}`} onClick={() => act.mutate({ key: p.key, status: "archived" })} className="rounded-lg p-2 hover:bg-accent"><Archive size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AddonManager addons={addons} qc={qc} />

      <div className="rounded-2xl border border-border bg-card p-5">
        <button data-testid="toggle-audit" onClick={() => setShowAudit((v) => !v)} className="text-sm font-semibold text-primary">{showAudit ? "Hide" : "Show"} price-change history</button>
        {showAudit && (
          <div className="mt-3 space-y-1 text-sm" data-testid="price-audit">
            {(auditData?.audit || []).length === 0 && <p className="text-muted-foreground">No price changes yet.</p>}
            {(auditData?.audit || []).map((a, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-accent/40 px-3 py-2">
                <span>{a.package_key} · {CYCLE_LABEL[a.cycle]}</span>
                <span>{money(a.old_cents)} → <b>{money(a.new_cents)}</b></span>
                <span className="text-xs text-muted-foreground">{a.effective === "next_renewal" ? "at next renewal" : "new customers only"} · {a.changed_by}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && <Editor draft={editing} setDraft={setEditing} schema={schema} onSave={() => save.mutate(cleanDraft(editing))} saving={save.isPending} />}
      {subsFor && <SubsModal data={subsFor} onClose={() => setSubsFor(null)} />}
    </div>
  );
}

const cleanDraft = (d) => ({ ...d });

function Editor({ draft, setDraft, schema, onSave, saving }) {
  const up = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setPrice = (c, v) => setDraft((d) => ({ ...d, pricing: { ...d.pricing, [c]: C(v) } }));
  const setLimit = (k, mode, num) => setDraft((d) => ({ ...d, limits: { ...d.limits, [k]: mode === "limited" ? (num ?? (typeof d.limits[k] === "number" ? d.limits[k] : 0)) : mode } }));
  const toggleFeat = (k) => setDraft((d) => ({ ...d, features: { ...d.features, [k]: !d.features[k] } }));

  return (
    <div className="fixed inset-0 z-[70]" data-testid="package-editor">
      <div className="absolute inset-0 bg-black/50" onClick={() => setDraft(null)} />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-lg flex-col overflow-y-auto bg-background p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">{draft._isNew ? "New package" : `Edit ${draft.name}`}</h2>
          <button onClick={() => setDraft(null)} className="rounded-full p-2 hover:bg-accent"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <Field label="Name"><input data-testid="pkg-name" value={draft.name} onChange={(e) => up({ name: e.target.value })} className={inp} /></Field>
          <Field label="Tagline"><input value={draft.tagline} onChange={(e) => up({ tagline: e.target.value })} className={inp} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Badge"><select value={draft.badge} onChange={(e) => up({ badge: e.target.value })} className={inp}>{(schema?.badges || []).map((b) => <option key={b} value={b}>{b || "None"}</option>)}</select></Field>
            <Field label="Status"><select data-testid="pkg-status" value={draft.status} onChange={(e) => up({ status: e.target.value })} className={inp}>{["active", "inactive", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.contact_sales} onChange={(e) => up({ contact_sales: e.target.checked })} /> Contact Sales (no direct checkout)</label>

          <div className="rounded-xl border border-border p-3">
            <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Pricing (USD)</p>
            <div className="grid grid-cols-2 gap-3">
              {schema?.cycles?.map((c) => (
                <Field key={c} label={CYCLE_LABEL[c]}><input type="number" data-testid={`price-${c}`} value={D(draft.pricing[c])} onChange={(e) => setPrice(c, e.target.value)} className={inp} /></Field>
              ))}
              <Field label="Setup fee"><input type="number" value={D(draft.setup_fee_cents)} onChange={(e) => up({ setup_fee_cents: C(e.target.value) })} className={inp} /></Field>
              <Field label="Trial days"><input type="number" value={draft.trial_days} onChange={(e) => up({ trial_days: parseInt(e.target.value) || 0 })} className={inp} /></Field>
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.promo?.enabled} onChange={(e) => up({ promo: { ...draft.promo, enabled: e.target.checked } })} /> Promotional monthly price</label>
            {draft.promo?.enabled && (
              <div className="mt-2 grid grid-cols-2 gap-3">
                <Field label="Promo $/mo"><input type="number" value={D(draft.promo.price_cents)} onChange={(e) => up({ promo: { ...draft.promo, price_cents: C(e.target.value) } })} className={inp} /></Field>
                <Field label="Promo label"><input value={draft.promo.label} onChange={(e) => up({ promo: { ...draft.promo, label: e.target.value } })} className={inp} /></Field>
              </div>
            )}
            {!draft._isNew && (
              <Field label="Apply price change to"><select value={draft.price_effective} onChange={(e) => up({ price_effective: e.target.value })} className={inp}>
                <option value="new_only">New customers only</option>
                <option value="next_renewal">All eligible subscribers at next renewal</option>
              </select></Field>
            )}
          </div>

          <div className="rounded-xl border border-border p-3">
            <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Features</p>
            <div className="grid grid-cols-2 gap-2">
              {schema?.feature_keys?.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid={`feat-${k}`} checked={!!draft.features[k]} onChange={() => toggleFeat(k)} /> {label}</label>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border p-3">
            <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Usage limits</p>
            <div className="space-y-2">
              {schema?.limit_keys?.map(([k, label]) => {
                const v = draft.limits[k];
                const mode = v === "unlimited" ? "unlimited" : v === "none" ? "none" : "limited";
                return (
                  <div key={k} className="flex items-center gap-2">
                    <span className="w-32 text-sm">{label}</span>
                    <select data-testid={`limit-mode-${k}`} value={mode} onChange={(e) => setLimit(k, e.target.value, 0)} className={`${inp} w-36`}>
                      <option value="limited">Limited</option><option value="unlimited">Unlimited</option><option value="none">Not included</option>
                    </select>
                    {mode === "limited" && <input type="number" data-testid={`limit-val-${k}`} value={typeof v === "number" ? v : 0} onChange={(e) => setLimit(k, "limited", parseInt(e.target.value) || 0)} className={`${inp} w-28`} />}
                  </div>
                );
              })}
            </div>
          </div>

          <Field label="Feature bullets (one per line, shown to customers)">
            <textarea data-testid="pkg-features-text" rows={5} value={(draft.feature_list || []).join("\n")} onChange={(e) => up({ feature_list: e.target.value.split("\n").filter(Boolean) })} className={inp} />
          </Field>
        </div>
        <button data-testid="save-package" onClick={onSave} disabled={saving || !draft.name} className="mt-5 rounded-full bg-primary py-3 font-semibold text-primary-foreground disabled:opacity-60">{saving ? "Saving…" : "Save package"}</button>
      </div>
    </div>
  );
}

function AddonManager({ addons, qc }) {
  const [draft, setDraft] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["ai-addons"] });
  const saveA = useMutation({
    mutationFn: async (a) => a._isNew ? (await api.post("/ai-sales/admin/addons", a)).data : (await api.put(`/ai-sales/admin/addons/${a.key}`, a)).data,
    onSuccess: () => { toast.success("Add-on saved"); setDraft(null); refresh(); }, onError: (e) => toast.error(apiError(e)),
  });
  const delA = useMutation({ mutationFn: async (key) => (await api.delete(`/ai-sales/admin/addons/${key}`)).data, onSuccess: () => { toast.success("Removed"); refresh(); } });

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="addon-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold">Add-ons &amp; overages</h2>
        <button data-testid="new-addon" onClick={() => setDraft({ _isNew: true, name: "", price_cents: 0, billing: "monthly", included_qty: 0, overage_rate_cents: 0, active: true, eligible_packages: [] })} className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm font-semibold"><Plus size={14} weight="bold" /> Add-on</button>
      </div>
      <div className="mt-3 space-y-2">
        {addons.length === 0 && <p className="text-sm text-muted-foreground">No add-ons yet.</p>}
        {addons.map((a) => (
          <div key={a.key} className="flex items-center justify-between rounded-xl bg-accent/40 px-3 py-2 text-sm">
            <div><span className="font-semibold">{a.name}</span> <span className="text-muted-foreground">· {money(a.price_cents)}/{a.billing}{a.included_qty ? ` · incl ${a.included_qty}` : ""}</span></div>
            <div className="flex gap-1">
              <button onClick={() => setDraft({ ...a, _isNew: false })} className="rounded-lg p-1.5 hover:bg-accent"><PencilSimple size={15} /></button>
              <button onClick={() => delA.mutate(a.key)} className="rounded-lg p-1.5 hover:bg-accent"><X size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      {draft && (
        <div className="mt-3 grid grid-cols-2 gap-3 rounded-xl border border-border p-3">
          <Field label="Name"><input data-testid="addon-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={inp} /></Field>
          <Field label="Price ($)"><input type="number" value={D(draft.price_cents)} onChange={(e) => setDraft({ ...draft, price_cents: C(e.target.value) })} className={inp} /></Field>
          <Field label="Billing"><select value={draft.billing} onChange={(e) => setDraft({ ...draft, billing: e.target.value })} className={inp}><option value="monthly">monthly</option><option value="annual">annual</option><option value="one_time">one_time</option></select></Field>
          <Field label="Included qty"><input type="number" value={draft.included_qty} onChange={(e) => setDraft({ ...draft, included_qty: parseInt(e.target.value) || 0 })} className={inp} /></Field>
          <Field label="Overage rate ($)"><input type="number" value={D(draft.overage_rate_cents)} onChange={(e) => setDraft({ ...draft, overage_rate_cents: C(e.target.value) })} className={inp} /></Field>
          <div className="col-span-2 flex gap-2">
            <button data-testid="save-addon" onClick={() => saveA.mutate(draft)} disabled={!draft.name} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Save</button>
            <button onClick={() => setDraft(null)} className="rounded-full border border-border px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function SubsModal({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="subs-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-background p-5">
        <div className="mb-3 flex items-center justify-between"><h3 className="font-semibold">Subscribers · {data.key}</h3><button onClick={onClose}><X size={18} /></button></div>
        <div className="max-h-80 space-y-2 overflow-y-auto">
          {data.rows.length === 0 && <p className="text-sm text-muted-foreground">No active subscribers.</p>}
          {data.rows.map((s, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg bg-accent/40 px-3 py-2 text-sm">
              <span>{s.name || s.email}</span><span className="text-muted-foreground">{money(s.price_cents || 0)}/{s.cycle}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const inp = "w-full rounded-lg border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary";
const Field = ({ label, children }) => (<label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>{children}</label>);
